/**
 * RequirementDetailPage — /requests/:id
 *
 * Read-only detail view for a single captured requirement. No edit or
 * archive controls live here — those stay on the List tab (admin-only).
 *
 * Three-state (loading skeleton / error+Retry / not-found) per ui-ux-pro-max,
 * plus a dedicated 404 state distinct from a generic fetch error.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AttachmentList } from '@/components/AttachmentList';
import { itemFromAttachment } from '@/hooks/useAttachmentUploads';
import { useRequirement } from '@/hooks/useRequirement';
import { useRequirementAttachments } from '@/hooks/useRequirementAttachments';
import type { RequirementOut } from '@/types/requirement';

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return iso;
  }
}

function deriveStatus(req: RequirementOut): { label: string; color: 'default' | 'success' } {
  return req.archived_at ? { label: 'Archived', color: 'default' } : { label: 'Open', color: 'success' };
}

interface MetaRowProps {
  label: string;
  children: ReactElement | string;
}

function MetaRow({ label, children }: MetaRowProps): ReactElement {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 140, flexShrink: 0, pt: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
      </Box>
    </Stack>
  );
}

function RequirementAttachments({ requirementId }: { requirementId: string }): ReactElement {
  const { data, isLoading, error, refetch } = useRequirementAttachments(requirementId);
  const items = data.map(itemFromAttachment);
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Attachments
      </Typography>
      {isLoading && <Skeleton variant="rounded" height={52} role="status" aria-label="Loading attachments" />}
      {!isLoading && error && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button size="small" color="inherit" onClick={refetch}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {!isLoading && !error && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No attachments
        </Typography>
      )}
      {!isLoading && !error && items.length > 0 && <AttachmentList items={items} readOnly />}
    </Box>
  );
}

function BackLink(): ReactElement {
  const navigate = useNavigate();
  return (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/requests')}
      sx={{ mb: 2, minHeight: 44 }}
    >
      Back to requests
    </Button>
  );
}

export function RequirementDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, notFound, refetch } = useRequirement(id);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <BackLink />

      {isLoading && (
        <Stack spacing={2} role="status" aria-label="Loading requirement">
          <Skeleton variant="text" width="60%" height={48} />
          <Skeleton variant="rounded" height={24} width="40%" />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={80} />
        </Stack>
      )}

      {!isLoading && notFound && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Requirement not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            It may have been removed, or the link is incorrect.
          </Typography>
        </Box>
      )}

      {!isLoading && !notFound && error && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button size="small" color="inherit" onClick={refetch}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!isLoading && !notFound && !error && data && (
        <Stack spacing={3}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Typography variant="h4" sx={{ flex: 1 }}>
              {data.problem}
            </Typography>
            {data.notion_url && (
              <Button
                variant="outlined"
                component="a"
                href={data.notion_url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                aria-label={`Open "${data.problem}" in Notion`}
                sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
              >
                Open in Notion
              </Button>
            )}
          </Stack>

          {data.expected_outcome && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Expected outcome
              </Typography>
              <Typography variant="body1">{data.expected_outcome}</Typography>
            </Box>
          )}

          {data.acceptance_criteria.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Acceptance criteria
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {data.acceptance_criteria.map((ac, i) => (
                  <Typography component="li" variant="body1" key={i} sx={{ mb: 0.5 }}>
                    {ac}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Details
            </Typography>
            <Stack spacing={1.5}>
              <MetaRow label="Status">
                <Chip label={deriveStatus(data).label} color={deriveStatus(data).color} size="small" />
              </MetaRow>
              <MetaRow label="Type">
                <Chip label={data.type} size="small" variant="outlined" />
              </MetaRow>
              <MetaRow label="Severity">{data.severity ?? '—'}</MetaRow>
              <MetaRow label="Size">{data.size ?? '—'}</MetaRow>
              <MetaRow label="Area">{data.area ?? '—'}</MetaRow>
              <MetaRow label="Projects">
                {data.projects.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {data.projects.map((p) => (
                      <Chip key={p} label={p} size="small" variant="outlined" color="primary" />
                    ))}
                  </Stack>
                ) : (
                  '—'
                )}
              </MetaRow>
              <MetaRow label="Requested by">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {data.requested_by}
                </Typography>
              </MetaRow>
              <MetaRow label="Created">{fmtDate(data.created_at)}</MetaRow>
            </Stack>
          </Box>

          <Divider />

          <RequirementAttachments requirementId={data.id} />
        </Stack>
      )}
    </Box>
  );
}
